import React, { FC, useEffect, useState } from 'react';
import { Dialog, Checkbox, FormControlLabel } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { formatFullPhoneNumber } from 'utils/formatPhoneNumber';
import {
  updateStep3,
  updateSelectedLastInsurance,
} from 'redux/bookingFormSlice';
import { getInsurances } from 'services/APIs';
import { Insurance } from 'interfaces/insuranceType';
import { renderToast } from 'components/CustomToast/CustomToast';
import DentalButton from 'components/Buttons/DentalButton/DentalButton';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { Asterix } from 'components/Form/HelperText/HelperText';
import { INSURANCE_PAYMENT_METHOD_OPTIONS } from 'pages/ClinicDetailsPage/BookNowPanel/utils/constants';
import { ReactComponent as CheckCircleIcon } from 'assets/icons/check_circle.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import { ReactComponent as DropdownIcon } from 'assets/icons/arrow-down.svg';
import { ModeOption } from 'pages/ClinicDetailsPage/BookNowPanel/utils/constants';
import styles from './InsuranceSelectionModal.module.scss';

interface InsuranceSelectionModalProps {
  clinicId: string;
  open: boolean;
  onClose: () => void;
}

const InsuranceSelectionModal: FC<InsuranceSelectionModalProps> = ({
  clinicId,
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  const {
    clinicDetailsSlice: {
      clinicData: { phone, name },
    },
  } = useAppSelector((state) => state);

  const [options, setOptions] = useState<Insurance[]>([]);

  const [isLoadingInsurances, setIsLoadingInsurances] = useState(false);

  const [selectedInsurance, setSelectedInsurance] = useState<Insurance | null>(
    null
  );

  const [isPayForMyself, setIsPayForMyself] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState<ModeOption | null>(null);

  const [practitionerInsuranceError, setPractitionerInsuranceError] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!open) return;

    const fetchInsurances = async () => {
      setIsLoadingInsurances(true);

      const data = await getInsurances(clinicId);

      setIsLoadingInsurances(false);

      if (data.error) return;

      setOptions(data.data ?? []);
    };

    fetchInsurances();
  }, [clinicId, open]);

  const renderInsuranceMessage = (insurance: Insurance) => {
    return insurance.isActive ? (
      <div className={styles['info']}>{`Insurance accepted by ${name}.`}</div>
    ) : (
      <div className={styles['inactive-warning']}>
        <span>
          {name} does not allow this insurance provider to schedule online.
        </span>
        <span>
          Please call us at{' '}
          <a href={`tel:+${phone}`}>{formatFullPhoneNumber(phone)} </a>
          to schedule your appointment.
        </span>
      </div>
    );
  };

  const handleOk = () => {
    if (!selectedInsurance && !isPayForMyself) {
      renderToast({
        type: 'error',
        message: 'Please select an insurance provider',
      });
      return;
    }

    const insuranceId = isPayForMyself ? '' : selectedInsurance!.insuranceId;

    if (!isPayForMyself) {
      dispatch(updateStep3({ currentInsurance: selectedInsurance }));
    } else {
      dispatch(
        updateStep3({
          currentInsurance: null,
          isPayForMyself: true,
          insurancePaymentMethod: paymentMethod,
        })
      );
    }

    dispatch(
      updateSelectedLastInsurance(
        insuranceId
          ? {
              clinicInsuranceId: selectedInsurance!.id,
              insuranceCarrierName: selectedInsurance!.name,
              insuranceCarrierId: insuranceId,
            }
          : null
      )
    );
    onClose();
  };

  const disabledButton = selectedInsurance
    ? !selectedInsurance.isActive
    : isPayForMyself
      ? paymentMethod === null
      : true;

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <section className={styles['section']}>
        <div className={styles['headerSection']}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div className={styles['title']}>Insurance Information</div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className={styles['bodySection']}>
          <h3>
            Insurance provider <Asterix error={'error'} />
          </h3>
          <Autocomplete
            options={options}
            value={selectedInsurance}
            getOptionLabel={(option) => option.name}
            classes={{
              option: styles['popper-insurance-item'],
              noOptions: styles['popper-insurance-item'],
            }}
            onChange={(_, value) => {
              setSelectedInsurance(value);
              setPractitionerInsuranceError(null);
            }}
            loading={isLoadingInsurances}
            disabled={isPayForMyself}
            renderInput={(params) => (
              <div
                className={styles['input-wrapper']}
                ref={params.InputProps.ref}
              >
                {selectedInsurance ? (
                  <>
                    <button
                      className={styles['selected-insurance']}
                      onClick={() => {
                        setSelectedInsurance(null);
                        setPractitionerInsuranceError(null);
                      }}
                    >
                      <div>
                        {practitionerInsuranceError ? (
                          <WarningIcon className={styles['warning-red']} />
                        ) : selectedInsurance.isActive ? (
                          <CheckCircleIcon className={styles['check-circle']} />
                        ) : (
                          <WarningIcon className={styles['warning']} />
                        )}
                        <span>{selectedInsurance.name}</span>
                      </div>
                    </button>
                    {practitionerInsuranceError ? (
                      <div className={styles['practitioner-warning']}>
                        <span>{practitionerInsuranceError}</span>
                      </div>
                    ) : (
                      renderInsuranceMessage(selectedInsurance)
                    )}
                  </>
                ) : (
                  <input
                    placeholder="Select Insurance Provider"
                    type="text"
                    {...params.inputProps}
                  />
                )}
              </div>
            )}
          />

          <div className={styles['checkbox-section']}>
            <FormControlLabel
              checked={isPayForMyself}
              onChange={() => {
                setIsPayForMyself(!isPayForMyself);
                if (!isPayForMyself) {
                  setSelectedInsurance(null);
                  setPaymentMethod(null);
                }
              }}
              classes={{ label: styles['label'] }}
              control={<Checkbox classes={{ input: styles['input'] }} />}
              label="I'm paying for myself"
            />
          </div>

          {isPayForMyself && (
            <div className={styles['insurance-payment-method-section']}>
              <Autocomplete
                options={INSURANCE_PAYMENT_METHOD_OPTIONS}
                value={paymentMethod}
                getOptionLabel={(option) => option?.label}
                classes={{
                  option: styles['popper-insurance-item'],
                  noOptions: styles['popper-insurance-item'],
                }}
                popupIcon={<DropdownIcon />}
                closeIcon={<></>}
                onChange={(_, value) => setPaymentMethod(value)}
                renderInput={(params) => (
                  <div
                    {...params.InputProps}
                    className={styles['input-wrapper']}
                  >
                    {paymentMethod ? (
                      <button
                        type="button"
                        className={styles['selected-insurance']}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPaymentMethod(null);
                        }}
                      >
                        <div>
                          <span>{paymentMethod.label}</span>
                        </div>
                      </button>
                    ) : (
                      <input
                        {...params.inputProps}
                        placeholder="Select payment option"
                        className={styles['input']}
                        readOnly
                      />
                    )}
                    <div
                      className={
                        styles['insurance-payment-method-section-arrow']
                      }
                    >
                      {params.InputProps.endAdornment}
                    </div>
                  </div>
                )}
              />
            </div>
          )}

          <div
            style={{
              marginTop: '2.4rem',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <DentalButton
              variant="contained"
              onClick={handleOk}
              disabled={disabledButton}
            >
              Ok
            </DentalButton>
          </div>
        </div>
      </section>
    </Dialog>
  );
};

export default InsuranceSelectionModal;
